import { MeterType, RULEBOOK_ID_PATTERN, RULEBOOK_RESERVED_IDS, } from "@next-platform/packages-ts-types";
import { MeterAiMode, RULEBOOK_LIMITS } from "@next-platform/packages-ts-types";
const MAX_SLUG_LENGTH = 32;
const PIPS_MAX_STEPS = 10;
const BAR_DEFAULT_MAX = 100;
const PIPS_DEFAULT_MAX = 5;
const METER_MIN = 0;
const DICE_DEFAULT_SIDES = 20;
const DICE_FIRST_FACE = 1;
/** Lowercase slug: non-alphanumerics become `-`, dashes trimmed, capped at 32 chars. */
export function slugifyRulebookId(text) {
    return text
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-+|-+$/g, "")
        .slice(0, MAX_SLUG_LENGTH)
        .replace(/-+$/g, "");
}
const nextSequentialId = (prefix, existingIds) => {
    let counter = 1;
    let candidate = prefix + counter;
    while (existingIds.includes(candidate)) {
        counter += 1;
        candidate = prefix + counter;
    }
    return candidate;
};
/** Slug of the label when unique, else `m<N>`; slugs that fail RULEBOOK_ID_PATTERN or are reserved get an `m-` prefix. */
export function generateMeterId({ label, existingIds }) {
    const slug = slugifyRulebookId(label);
    const candidate = slug && (!RULEBOOK_ID_PATTERN.test(slug) || RULEBOOK_RESERVED_IDS.includes(slug)) ? "m-" + slug : slug;
    if (candidate && !existingIds.includes(candidate)) {
        return candidate;
    }
    return nextSequentialId("m", existingIds);
}
export function generateRuleId({ existingIds }) {
    return nextSequentialId("r", existingIds);
}
export function generateAiRowId({ existingIds }) {
    return nextSequentialId("a", existingIds);
}
/** First free draft id: `rulebook`, then `rulebook-2`, `rulebook-3`, … */
export function uniqueRulebookDraftId(existingIds, base = "rulebook") {
    let candidate = base;
    let counter = 2;
    while (existingIds.includes(candidate)) {
        candidate = `${base}-${counter}`;
        counter += 1;
    }
    return candidate;
}
export function createEmptyRulebookDefinition({ id, name }) {
    return { id, name, description: "", meters: [], combined_rules: [] };
}
export function createDefaultLevel({ label, to }) {
    return { label, to, instruction: "", caption: "", revealed: true, override: false };
}
const createDefaultBounded = ({ type, id, label }) => ({
    id,
    type,
    label,
    min: METER_MIN,
    max: type === MeterType.PIPS ? PIPS_DEFAULT_MAX : BAR_DEFAULT_MAX,
    default: 0,
    decay: 0,
    keywords: [],
    levels: type === MeterType.PIPS
        ? [createDefaultLevel({ label: "Low", to: 2 }), createDefaultLevel({ label: "High", to: 5 })]
        : [createDefaultLevel({ label: "Low", to: 50 }), createDefaultLevel({ label: "High", to: 100 })],
    tracks: "chat",
    visible: true,
});
const createDefaultCounter = ({ id, label }) => ({
    id,
    type: MeterType.NUMBER,
    label,
    default: 0,
    decimals: 0,
    display: "number",
    per_message: 0,
    spendable: false,
    tell_model: false,
    keywords: [],
    levels: [createDefaultLevel({ label: "Start", to: null })],
    tracks: "chat",
    visible: true,
});
/** Four six-hour parts of the day; bounds are minutes since 06:00, the last one closing at 1439. */
export function createDefaultTodLevels() {
    return [
        createDefaultLevel({ label: "Morning", to: 359 }),
        createDefaultLevel({ label: "Afternoon", to: 719 }),
        createDefaultLevel({ label: "Evening", to: 1079 }),
        createDefaultLevel({ label: "Night", to: 1439 }),
    ];
}
/** The first story days, then everything after (open-ended). */
export function createDefaultDayLevels() {
    return [createDefaultLevel({ label: "First days", to: 3 }), createDefaultLevel({ label: "Later", to: null })];
}
const createDefaultClock = ({ id, label }) => ({
    id,
    type: MeterType.CLOCK,
    label,
    mode: "datetime",
    // "Day 3 · 18:40"; the calendar date (and `start`'s date) is opt-in in the Studio.
    display: "daycount",
    start: "2026-01-01T12:00",
    step_minutes: 15,
    step_every_messages: 0,
    detect_skips: true,
    tell_model: true,
    rule_dimensions: ["tod"],
    tod_levels: createDefaultTodLevels(),
    day_levels: [],
    tracks: "chat",
    visible: true,
});
/** Fumble on a 1, critical on the top face, everything between is a normal roll. */
const diceLevelsFor = (sides) => [
    createDefaultLevel({ label: "Fumble", to: DICE_FIRST_FACE }),
    createDefaultLevel({ label: "Normal", to: sides - 1 }),
    createDefaultLevel({ label: "Critical", to: sides }),
];
const createDefaultDice = ({ id, label }) => ({
    id,
    type: MeterType.DICE,
    label,
    sides: DICE_DEFAULT_SIDES,
    levels: diceLevelsFor(DICE_DEFAULT_SIDES),
    tracks: "chat",
    visible: true,
});
export function createDefaultMeter(options) {
    if (options.type === MeterType.DICE) {
        return createDefaultDice(options);
    }
    if (options.type === MeterType.CLOCK) {
        return createDefaultClock(options);
    }
    if (options.type === MeterType.NUMBER) {
        return createDefaultCounter(options);
    }
    return createDefaultBounded({ ...options, type: options.type });
}
const commonFields = (meter) => ({
    id: meter.id,
    label: meter.label,
    icon: meter.icon,
    color: meter.color,
    tracks: meter.tracks,
    visible: meter.visible,
    tells_users: meter.tells_users,
});
const clamp = (value, lo, hi) => Math.max(lo, Math.min(hi, value));
/**
 * Re-range levels into `[min, max]`: intermediates are clamped strictly increasing
 * (levels that cannot fit are dropped); the final level closes at `to === null` or `max`.
 */
const rescaleLevels = (levels, min, max) => {
    if (!levels.length) {
        return [createDefaultLevel({ label: "Level 1", to: max })];
    }
    const rescaled = [];
    let prevTo = min - 1;
    const intermediates = levels.slice(0, -1);
    intermediates.forEach((level) => {
        if (max === null) {
            rescaled.push({ ...level, to: Math.max(prevTo + 1, level.to ?? prevTo + 1) });
            prevTo = rescaled[rescaled.length - 1]?.to ?? prevTo;
            return;
        }
        if (prevTo + 1 > max - 1) {
            return;
        }
        const nextTo = clamp(level.to ?? prevTo + 1, prevTo + 1, max - 1);
        rescaled.push({ ...level, to: nextTo });
        prevTo = nextTo;
    });
    const last = levels[levels.length - 1];
    if (!last) {
        rescaled.push(createDefaultLevel({ label: "Level 1", to: max }));
        return rescaled;
    }
    rescaled.push({ ...last, to: max });
    return rescaled;
};
const convertBounded = (meter, type) => {
    const common = commonFields(meter);
    const min = meter.min;
    let max = meter.max;
    if (type === MeterType.PIPS && max - min > PIPS_MAX_STEPS) {
        max = min + PIPS_MAX_STEPS;
    }
    return {
        ...common,
        type,
        min,
        max,
        default: clamp(meter.default, min, max),
        decay: meter.decay,
        ...(meter.decay_every !== undefined ? { decay_every: meter.decay_every } : {}),
        keywords: meter.keywords,
        ai: meter.ai,
        levels: rescaleLevels(meter.levels, min, max),
    };
};
const boundedToCounter = (meter) => ({
    ...commonFields(meter),
    type: MeterType.NUMBER,
    default: meter.default,
    display: "number",
    per_message: 0,
    spendable: false,
    tell_model: false,
    keywords: meter.keywords,
    ai: meter.ai,
    levels: rescaleLevels(meter.levels, meter.min, null),
});
const roundAwayFromZero = (value) => Math.sign(value) * Math.round(Math.abs(value));
/**
 * Extract mode is number-only: converted meters fall back to rules rows and the narrower default caps.
 * With no rows to ask about, the driver is switched off rather than left enabled-but-empty (an error).
 */
const counterAiToBounded = (ai) => {
    if (ai.mode !== MeterAiMode.EXTRACT) {
        return { ...ai, per_turn_min: roundAwayFromZero(ai.per_turn_min), per_turn_max: roundAwayFromZero(ai.per_turn_max) };
    }
    const isExtractDefaultCaps = ai.per_turn_min === -RULEBOOK_LIMITS.EXTRACT_PER_TURN_CAP && ai.per_turn_max === RULEBOOK_LIMITS.EXTRACT_PER_TURN_CAP;
    return {
        enabled: ai.enabled && ai.rows.length > 0,
        rows: ai.rows,
        negative_overrides_positive: ai.negative_overrides_positive,
        per_turn_min: isExtractDefaultCaps ? -RULEBOOK_LIMITS.RULES_PER_TURN_CAP : roundAwayFromZero(ai.per_turn_min),
        per_turn_max: isExtractDefaultCaps ? RULEBOOK_LIMITS.RULES_PER_TURN_CAP : roundAwayFromZero(ai.per_turn_max),
        mode: MeterAiMode.RULES,
    };
};
const counterToBounded = (meter, type) => {
    const min = METER_MIN;
    const max = type === MeterType.PIPS ? PIPS_DEFAULT_MAX : BAR_DEFAULT_MAX;
    return {
        ...commonFields(meter),
        type,
        min,
        max,
        default: clamp(roundAwayFromZero(meter.default), min, max),
        decay: 0,
        keywords: meter.keywords.map((driver) => ({ ...driver, delta: roundAwayFromZero(driver.delta) })),
        ai: meter.ai === undefined ? undefined : counterAiToBounded(meter.ai),
        levels: rescaleLevels(meter.levels.map((level) => (level.to === null ? level : { ...level, to: roundAwayFromZero(level.to) })), min, max),
    };
};
const toDice = (meter) => ({
    ...createDefaultDice({ type: MeterType.DICE, id: meter.id, label: meter.label }),
    ...commonFields(meter),
});
const diceToValueMeter = (meter, type) => {
    const common = commonFields(meter);
    if (type === MeterType.NUMBER) {
        return { ...createDefaultCounter({ type, id: meter.id, label: meter.label }), ...common, levels: rescaleLevels(meter.levels, 0, null) };
    }
    const max = type === MeterType.PIPS ? Math.min(meter.sides, DICE_FIRST_FACE + PIPS_MAX_STEPS) : meter.sides;
    return {
        ...createDefaultBounded({ type, id: meter.id, label: meter.label }),
        ...common,
        min: DICE_FIRST_FACE,
        max,
        default: DICE_FIRST_FACE,
        levels: rescaleLevels(meter.levels, DICE_FIRST_FACE, max),
    };
};
/** Convert a meter to another type, keeping common fields and re-ranging levels. */
export function convertMeterType({ meter, type }) {
    if (meter.type === type) {
        return meter;
    }
    if (type === MeterType.CLOCK || meter.type === MeterType.CLOCK) {
        const fresh = createDefaultMeter({ type, id: meter.id, label: meter.label });
        return { ...fresh, ...commonFields(meter) };
    }
    if (type === MeterType.DICE) {
        return toDice(meter);
    }
    if (meter.type === MeterType.DICE) {
        return diceToValueMeter(meter, type);
    }
    if (meter.type === MeterType.NUMBER) {
        return counterToBounded(meter, type === MeterType.NUMBER ? MeterType.BAR : type);
    }
    if (type === MeterType.NUMBER) {
        return boundedToCounter(meter);
    }
    return convertBounded(meter, type);
}
/**
 * Resize a die, stretching its levels proportionally so a fumble stays on 1 and a critical on the top face.
 * Intermediate bounds stay strictly increasing; levels that no longer fit are dropped.
 */
export function resizeDiceMeter({ meter, sides }) {
    if (sides === meter.sides) {
        return meter;
    }
    const scale = (to) => Math.round((to / meter.sides) * sides);
    const stretched = meter.levels.map((level, index) => {
        const isFirstFace = index === 0 && level.to === DICE_FIRST_FACE;
        return { ...level, to: isFirstFace ? DICE_FIRST_FACE : scale(level.to ?? meter.sides) };
    });
    return { ...meter, sides, levels: rescaleLevels(stretched, DICE_FIRST_FACE, sides) };
}
export function createDefaultCombinedRule({ id }) {
    return { id, conditions: [], instruction: "", caption: "" };
}
